import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Attachment, Project } from '../../types/project'
import type { CreateTagData } from '../../types/tag'
import type { WorkOrder } from '../../types/workorder'
import DatePicker from '../DatePicker'
import GooglePlacesSelect from '../GooglePlacesSelect'
import NoteAttachments from '../NoteAttachments'
import TimePicker from '../Timepicker'

// Helper function to format time from datetime string
function formatTimeFromDateTime(dateTimeString: string): string {
  const date = new Date(dateTimeString)
  let hours = date.getHours()
  const minutes = date.getMinutes()
  const period = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`
}

interface JobDetailsStepProps {
  onUpdate: (data: Partial<CreateTagData>) => void
  data: Partial<CreateTagData>
  relatedWorkorder?: { id: string } & Partial<WorkOrder>
  relatedProject?: Project
  onValidationChange?: (isValid: boolean, errorMessage?: string) => void
  showErrors?: boolean
  // Attachment props
  attachments?: Attachment[]
  pendingAttachments?: File[]
  onPendingAttachmentsChange?: (files: File[]) => void
  onAttachmentUpload?: (files: File[]) => Promise<void>
  onAttachmentDelete?: (id: number) => Promise<void>
}

const JobDetailsStep: React.FC<JobDetailsStepProps> = ({
  data,
  onUpdate,
  relatedWorkorder,
  relatedProject,
  onValidationChange,
  showErrors = false,
  attachments = [],
  pendingAttachments = [],
  onPendingAttachmentsChange,
  onAttachmentUpload,
  onAttachmentDelete,
}) => {
  const [minDate, setMinDate] = useState('')
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    data.start_date ? new Date(data.start_date) : null,
  )
  const [selectedTime, setSelectedTime] = useState<string | null>(
    data.start_date ? formatTimeFromDateTime(data.start_date) : null,
  )
  const [isOpen, setIsOpen] = useState(false)
  const [isValid, setIsValid] = useState<boolean>(false)

  // Helper function to combine date and time into ISO string
  const combineDateTime = useCallback(
    (date: Date | null, time: string | null): string | null => {
      if (!date) return null

      if (!time) {
        // Default to 8:00 AM if no time selected
        const combined = new Date(date)
        combined.setHours(8, 0, 0, 0)
        return combined.toISOString()
      }

      const [timePart, period] = time.split(' ')
      const [hourStr, minuteStr] = timePart.split(':')
      let hour = Number.parseInt(hourStr)
      const minute = Number.parseInt(minuteStr)

      if (period === 'PM' && hour !== 12) {
        hour += 12
      } else if (period === 'AM' && hour === 12) {
        hour = 0
      }

      const combined = new Date(date)
      combined.setHours(hour, minute, 0, 0)
      return combined.toISOString()
    },
    [],
  )

  useEffect(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')

    setMinDate(`${year}-${month}-${day}`)
  }, [])

  // Update start_date when date or time changes
  useEffect(() => {
    const combinedDateTime = combineDateTime(selectedDate, selectedTime)
    if (combinedDateTime !== data.start_date) {
      if (combinedDateTime) {
        onUpdate({ start_date: combinedDateTime })
      }
    }
  }, [selectedDate, selectedTime, data.start_date, onUpdate, combineDateTime])

  // No autopopulation from related work order (only enforce minDate at render)

  // Validation: required fields must be present
  useEffect(() => {
    const missingFields: string[] = []

    if (!data.title || data.title.trim() === '') missingFields.push('Title')
    if (!data.origin_name || data.origin_name.trim() === '')
      missingFields.push('Site Name')
    if (!data.origin_address || data.origin_address.trim() === '')
      missingFields.push('Pick Up Location')
    if (!data.destination_address || data.destination_address.trim() === '')
      missingFields.push('Drop Off Location')
    if (!data.material || data.material.trim() === '')
      missingFields.push('Material')
    if (!data.start_date || data.start_date.trim() === '')
      missingFields.push('Start Date')

    const nextValid = missingFields.length === 0
    setIsValid(nextValid)

    if (onValidationChange) {
      const errorMessage =
        missingFields.length > 0
          ? `Please fill in: ${missingFields.join(', ')}`
          : undefined
      onValidationChange(nextValid, errorMessage)
    }
  }, [
    data.title,
    data.origin_name,
    data.origin_address,
    data.destination_address,
    data.material,
    data.start_date,
    onValidationChange,
  ])

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date)
  }

  const handleSelect = () => {
    setIsOpen(false)
  }

  const handleTimeChange = (time: string) => {
    setSelectedTime(time)
  }

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>JOB DETAILS</h3>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Title <span className={css(styles.required)}>*</span>
        </label>
        <input
          type="text"
          value={data.title || ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="Enter tag title"
          className={css(styles.input)}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Site Name <span className={css(styles.required)}>*</span>
        </label>
        <input
          type="text"
          value={data.origin_name || ''}
          onChange={(e) => onUpdate({ origin_name: e.target.value })}
          placeholder="Enter site name"
          className={css(styles.input)}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Pick Up Location <span className={css(styles.required)}>*</span>
        </label>
        <GooglePlacesSelect
          value={
            data.origin_address
              ? { label: data.origin_address, value: data.origin_address }
              : null
          }
          onChange={(option) =>
            onUpdate({ origin_address: option?.value || '' })
          }
          placeholder="Enter pick up address"
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Drop Off Location <span className={css(styles.required)}>*</span>
        </label>
        <GooglePlacesSelect
          value={
            data.destination_address
              ? {
                  label: data.destination_address,
                  value: data.destination_address,
                }
              : null
          }
          onChange={(option) =>
            onUpdate({ destination_address: option?.value || '' })
          }
          placeholder="Enter destination address"
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Material <span className={css(styles.required)}>*</span>
        </label>
        <input
          type="text"
          value={data.material || ''}
          onChange={(e) => onUpdate({ material: e.target.value })}
          placeholder="Enter material type"
          className={css(styles.input)}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Notes (optional)</label>
        <textarea
          value={data.notes || ''}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          placeholder="Enter any additional notes"
          className={css(styles.textarea)}
          rows={4}
        />
      </div>

      {/* Attachments */}
      {onPendingAttachmentsChange && (
        <div className={css(styles.formGroup)}>
          <label className={css(styles.label)}>Attachments (optional)</label>
          <NoteAttachments
            attachments={attachments}
            pendingFiles={pendingAttachments}
            onPendingFilesChange={onPendingAttachmentsChange}
            onUpload={onAttachmentUpload}
            onDelete={onAttachmentDelete}
          />
        </div>
      )}

      <div className={css(styles.row)}>
        <div className={css(styles.formGroup, styles.halfWidth)}>
          <label className={css(styles.label)}>
            Start Date <span className={css(styles.required)}>*</span>
          </label>
          <input
            type="text"
            value={selectedDate ? selectedDate.toLocaleDateString() : ''}
            onClick={() => setIsOpen(true)}
            placeholder="Select start date"
            readOnly
            className={css(styles.input)}
          />
          <DatePicker
            selected={selectedDate}
            onChange={handleDateSelect}
            onSelect={handleSelect}
            minDate={
              relatedWorkorder?.starting_date
                ? new Date(relatedWorkorder.starting_date)
                : new Date()
            }
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
          />
        </div>
        <div className={css(styles.formGroup, styles.halfWidth)}>
          <TimePicker
            value={selectedTime}
            onChange={handleTimeChange}
            label="Start Time (optional)"
            inModal={true}
          />
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '24px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  required: {
    color: '#DC2626',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '14px',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '14px',
    resize: 'vertical',
  },
  row: {
    display: 'flex',
    gap: '16px',
  },
  halfWidth: {
    flex: 1,
  },
  calendarFooter: {
    padding: '8px',
    borderTop: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'center',
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '8px',
    fontWeight: 500,
    textAlign: 'center',
    padding: '8px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '6px',
  },
  selectButton: {
    padding: '8px 16px',
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    width: '100%',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#E5E7EB',
      cursor: 'not-allowed',
    },
  },
})

export default JobDetailsStep
